import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Menu, ShieldCheck, X } from "lucide-react";
import { AuthContext } from "../AuthContext";

function Navbar() {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const { token, logout } = useContext(AuthContext);
  const isAuthenticated = Boolean(token);
  const brandLogoSrc = `${process.env.PUBLIC_URL}/mekedge-logo.svg`;

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
    { name: "Pricing", path: "/pricing" },
    { name: "FAQ", path: "/faq" },
  ];

  return (
    <nav className="fixed inset-x-0 top-0 z-[120] border-b border-black/5 bg-[#f8f5ef]/90 backdrop-blur dark:border-white/10 dark:bg-[#020617]/85">
      <div className="mx-auto flex h-[80px] max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" className="flex items-center gap-3 text-slate-950 dark:text-white">
          <img
            src={brandLogoSrc}
            alt="MekEdge logo"
            className="h-11 w-11 object-contain"
          />
          <div>
            <p className="text-lg font-semibold tracking-tight">MekEdge</p>
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-400">Trading journal</p>
          </div>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-medium transition ${
                location.pathname === link.path
                  ? "text-slate-950 dark:text-white"
                  : "text-slate-500 dark:text-slate-300"
              } hover:text-slate-950 dark:hover:text-white`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        <div className="hidden items-center gap-3 md:flex">
          <div className="rounded-full border border-slate-300/70 bg-white/70 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            Built for serious traders
          </div>
          {isAuthenticated ? (
            <>
              <Link
                to="/app"
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={logout}
                className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
              >
                Login
              </Link>
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Sign Up
                <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
        </div>

        <button
          className="rounded-full border border-slate-300 bg-white p-3 text-slate-700 transition hover:bg-slate-50 md:hidden dark:border-white/10 dark:bg-white/5 dark:text-slate-100"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {menuOpen && (
        <div className="fixed inset-x-0 top-[80px] z-[125] max-h-[calc(100vh-80px)] overflow-y-auto border-t border-slate-200 bg-white/95 px-6 py-5 shadow-[0_20px_50px_-30px_rgba(15,23,42,0.7)] md:hidden dark:border-white/10 dark:bg-[#020617]/95">
          <div className="mb-4 flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
            <ShieldCheck className="h-4 w-4" />
            Trade journal and analytics
          </div>
          <div className="space-y-3">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setMenuOpen(false)}
              className={`block rounded-2xl px-4 py-3 text-sm font-medium transition ${
                location.pathname === link.path
                  ? "bg-slate-950 text-white dark:bg-white dark:text-slate-950"
                  : "text-slate-600 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-white/5"
              }`}
            >
              {link.name}
            </Link>
          ))}
          </div>
          <div className="mt-4 grid gap-3 border-t border-slate-200 pt-4 dark:border-white/10">
          {isAuthenticated ? (
            <>
              <Link
                to="/app"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
              >
                Dashboard
              </Link>
              <button
                type="button"
                onClick={() => {
                  logout();
                  setMenuOpen(false);
                }}
                className="inline-flex items-center justify-center rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center rounded-full border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-white/5 dark:text-slate-100 dark:hover:bg-white/10"
              >
                Login
              </Link>
              <Link
                to="/signup"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 dark:bg-white dark:text-slate-950 dark:hover:bg-slate-200"
              >
                Sign Up
                <ArrowRight className="h-4 w-4" />
              </Link>
            </>
          )}
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;